/**
 * Internal object metadata field ids (Allsetra.Domain.Constants.Objects.InternalFields).
 * Used when search results include metadata rows for fleet number / running hours.
 */
export const OBJECT_FLEET_NUMBER_FIELD_ID =
  "0aff358d-2c5f-43c9-bce8-aa2a782750d7";

export const OBJECT_RUNNING_HOURS_FIELD_ID =
  "3b0a3e75-acbc-4d5e-963e-b653fd796be0";

export type ObjectMetadataLike = {
  field?: { uniqueId?: string | null; label?: string | null } | null;
  value?: unknown;
};

const normalizeId = (value?: string | null) =>
  (value || "").trim().toLowerCase();

const metadataValueAsString = (value: unknown): string => {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value).trim();
};

export const getMetadataValueByFieldId = (
  metadata: ObjectMetadataLike[] | null | undefined,
  fieldUniqueId: string
): string => {
  if (!metadata?.length || !fieldUniqueId) return "";
  const target = normalizeId(fieldUniqueId);
  const match = metadata.find(
    (item) => normalizeId(item?.field?.uniqueId) === target
  );
  return metadataValueAsString(match?.value);
};

export const getMetadataValueByLabel = (
  metadata: ObjectMetadataLike[] | null | undefined,
  label: string
): string => {
  if (!metadata?.length || !label) return "";
  const target = normalizeId(label);
  const match = metadata.find(
    (item) => normalizeId(item?.field?.label) === target
  );
  return metadataValueAsString(match?.value);
};

export const getObjectFleetNumber = (object: {
  metadata?: ObjectMetadataLike[] | null;
}): string => {
  const byId = getMetadataValueByFieldId(
    object?.metadata,
    OBJECT_FLEET_NUMBER_FIELD_ID
  );
  if (byId) return byId;
  return getMetadataValueByLabel(object?.metadata, "fleet number");
};

export const getObjectRunningHours = (object: {
  metadata?: ObjectMetadataLike[] | null;
}): string =>
  getMetadataValueByFieldId(object?.metadata, OBJECT_RUNNING_HOURS_FIELD_ID);

export const getObjectMileageDisplay = (object: {
  lastReportedMileage?: number | null;
  mileage?: number | string | null;
}): string => {
  const lastReported = object?.lastReportedMileage;
  if (typeof lastReported === "number" && Number.isFinite(lastReported)) {
    return String(lastReported);
  }
  const mileage = object?.mileage;
  if (typeof mileage === "number" && Number.isFinite(mileage)) {
    return String(mileage);
  }
  if (typeof mileage === "string" && mileage.trim()) {
    return mileage.trim();
  }
  return "";
};

export const formatObjectDeviceIds = (
  devices?: Array<{ serialNumber?: string | null }> | null
): string => {
  if (!devices?.length) return "";
  return devices
    .map((device) => device?.serialNumber?.trim())
    .filter(Boolean)
    .join(", ");
};
